import { useState } from "react";
import {
  ArrowUp,
  ArrowUpRight,
  GraduationCap,
  Mail,
  MapPin,
  Phone,
  Send,
  ShieldCheck,
} from "lucide-react";
import app from "../../../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";
import "./ResponsiveFooter.css";
import footerLogo from "../../../images/imageNewLogo.webp";
import chartThree from "../../../images/Chart 3.webp";

const socialLinks = [
  {
    href: "https://www.facebook.com/TPCPerformanceCentre",
    src: "https://firebasestorage.googleapis.com/v0/b/tech-performance-centre.firebasestorage.app/o/signatures%2F10464408.webp?alt=media",
    alt: "Facebook",
  },
  {
    href: "https://www.instagram.com/tpcperformancecentre/",
    src: "https://firebasestorage.googleapis.com/v0/b/tech-performance-centre.firebasestorage.app/o/signatures%2Finsta.webp?alt=media",
    alt: "Instagram",
  },
  {
    href: "https://www.linkedin.com/company/107660745/admin/dashboard/",
    src: "https://firebasestorage.googleapis.com/v0/b/tech-performance-centre.firebasestorage.app/o/signatures%2Flinked.webp?alt=media",
    alt: "LinkedIn",
  },
  {
    href: "https://whatsapp.com",
    src: "https://firebasestorage.googleapis.com/v0/b/tech-performance-centre.firebasestorage.app/o/signatures%2Fwhatsapp.webp?alt=media",
    alt: "WhatsApp",
  },
  {
    href: "https://x.com/TPCpta",
    src: "https://firebasestorage.googleapis.com/v0/b/tech-performance-centre.firebasestorage.app/o/signatures%2F10464257.webp?alt=media",
    alt: "X",
  },
];

const footerLinks = [
  {
    title: "Explore",
    trend: "gold",
    links: [{ label: "About Us", href: "/about" }],
  },
  {
    title: "Bootcamp",
    trend: "green",
    links: [
      {
        label: "Price Action Trader Bootcamp",
        href: "/#tpc-price-action-trader-bootcamp-details",
      },
      {
        label: "Apply Now",
        href: "https://andus.co.za/trade-register",
        external: true,
      },
      {
        label: "Student Login",
        href: "https://andus.co.za/trade-login",
        external: true,
      },
    ],
  },
  {
    title: "Legal",
    trend: "red",
    links: [
      { label: "POPI Act", href: "https://popia.co.za/", external: true },
    ],
  },
];

const contactItems = [
  {
    id: "campus",
    icon: Phone,
    trend: "green",
    label: "Campus",
    value: "012 051 3483",
    href: "tel:+27120513483",
  },
  {
    id: "support",
    icon: Phone,
    trend: "gold",
    label: "Student Support",
    value: "+27 82 732 3216",
    href: "tel:+27827323216",
  },
  {
    id: "email",
    icon: Mail,
    trend: "red",
    label: "Email",
    value: "info@tpc.training",
    href: "mailto:info@tpc.training",
  },
  {
    id: "location",
    icon: MapPin,
    trend: "green",
    label: "Campus Location",
    value:
      "Floor 2, Landmark Building, 13 Umgazi Rd, Menlo Park, Pretoria, 0081",
  },
];

const complianceItems = [
  {
    id: "dhet",
    trend: "gold",
    label: "Department of Higher Education",
    value: "Registration number: 2009/FET07/088",
  },
  {
    id: "qcto",
    trend: "green",
    label: "QCTO Accreditation",
    value: "Accreditation number: 2006/021703/07",
  },
];

const programmeOptions = ["TPC - Price Action Trader Bootcamp"];

const marketCandles = [
  "green",
  "green",
  "red",
  "green",
  "gold",
  "red",
  "green",
  "green",
  "red",
  "green",
  "gold",
  "green",
];

function SmartFooterLink({ href, external, children, className = "" }) {
  if (external) {
    return (
      <a
        href={href}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
      >
        <span>{children}</span>
        <ArrowUpRight size={14} aria-hidden="true" />
      </a>
    );
  }

  return (
    <a href={href} className={className}>
      <span>{children}</span>
    </a>
  );
}

function ResponsiveFooter() {
  const [fullName, setFullName] = useState("");
  const [programme, setProgramme] = useState("");
  const [email, setEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");

  const currentYear = new Date().getFullYear();

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!fullName.trim() || !email.trim() || !programme) {
      setSubmitStatus("Please complete all fields.");
      return;
    }

    setIsSending(true);
    setSubmitStatus("");

    try {
      const callableFunctions = getFunctions(app, "us-central1");
      const sendEmail = httpsCallable(callableFunctions, "sendBrochureEmail");

      const subject = `Website enquiry: ${programme}`;
      const text = [
        "New website enquiry (Send Us an email form)",
        "",
        `Full name: ${fullName}`,
        `Email: ${email}`,
        `Programme of interest: ${programme}`,
        "",
        "Message:",
        "I would like more information about Trade Performance Centre.",
      ].join("\n");

      const result = await sendEmail({
        to: "marnus@tpc.training, wesley@tpc.training",
        from: "TPC Website <noreply@mail.andus.co.za>",
        subject,
        isHTML: false,
        text,
      });

      if (result?.data?.success) {
        setSubmitStatus("Email sent successfully.");
        setFullName("");
        setEmail("");
        setProgramme("");
      } else {
        setSubmitStatus("Failed to send email. Please try again.");
      }
    } catch (error) {
      console.error("Footer email error:", error);
      setSubmitStatus("An error occurred. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const scrollToTop = () => {
    if (typeof window === "undefined") return;

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <footer className="trpc-siteFooter" aria-labelledby="trpc-siteFooter-title">
      <div className="trpc-siteFooter__marketLayer" aria-hidden="true">
        <span className="trpc-siteFooter__priceLine trpc-siteFooter__priceLine--one" />
        <span className="trpc-siteFooter__priceLine trpc-siteFooter__priceLine--two" />

        <div className="trpc-siteFooter__candleGroup">
          {marketCandles.map((trend, index) => (
            <span
              key={`${trend}-${index}`}
              className="trpc-siteFooter__candle"
              data-trend={trend}
            />
          ))}
        </div>
      </div>

      <div className="trpc-siteFooter__shell">
        <div className="trpc-siteFooter__glow trpc-siteFooter__glowOne" />
        <div className="trpc-siteFooter__glow trpc-siteFooter__glowTwo" />

        <div className="trpc-siteFooter__terminalTop" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>

        <div className="trpc-siteFooter__top">
          <div className="trpc-siteFooter__brand">
            <a
              href="/"
              className="trpc-siteFooter__logoLink"
              aria-label="Trade Performance Centre homepage"
            >
              <span className="trpc-siteFooter__brandMark">
                <span className="trpc-siteFooter__logoPlate" aria-hidden="true">
                  <img
                    src={footerLogo}
                    alt=""
                    className="trpc-siteFooter__logo"
                    loading="lazy"
                    decoding="async"
                  />
                </span>

                <span className="trpc-siteFooter__wordmark">
                  <span className="trpc-siteFooter__chartBg" aria-hidden="true">
                    <img
                      src={chartThree}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  </span>

                  <span className="trpc-siteFooter__brandName">
                    <span className="trpc-siteFooter__brandHighlight">
                      Trade
                    </span>{" "}
                    Performance Centre
                  </span>
                </span>
              </span>
            </a>

            <div className="trpc-siteFooter__kicker">
              Price Action Trading Education
            </div>

            <h2 id="trpc-siteFooter-title" className="trpc-siteFooter__title">
              No shortcuts. No signals. Learn the market from first principles.
            </h2>

            <p className="trpc-siteFooter__lead">
              Practical trading education, live support, funded-account pathways
              and a route toward a Diploma in Financial Trading.
            </p>

            <div className="trpc-siteFooter__ctaRow">
              <a
                href="https://andus.co.za/trade-register"
                target="_blank"
                rel="noopener noreferrer"
                className="trpc-siteFooter__cta trpc-siteFooter__ctaPrimary"
                data-candle="green"
              >
                <span>Apply Now</span>
                <ArrowUpRight size={17} aria-hidden="true" />
              </a>

              <a
                href="/about#tpc-contact-us"
                className="trpc-siteFooter__cta trpc-siteFooter__ctaSecondary"
                data-candle="red"
              >
                <span>Contact Us</span>
              </a>
            </div>
          </div>

          <div className="trpc-siteFooter__enquiryCard">
            <div className="trpc-siteFooter__enquiryHeader">
              <span className="trpc-siteFooter__enquiryIcon" aria-hidden="true">
                <Send size={19} />
              </span>

              <div>
                <span className="trpc-siteFooter__eyebrow">Quick enquiry</span>
                <h3>Send us a message</h3>
              </div>
            </div>

            <form className="trpc-siteFooter__form" onSubmit={handleSubmit}>
              <div className="trpc-siteFooter__formGrid">
                <label className="trpc-siteFooter__field">
                  <span>Full name</span>
                  <input
                    type="text"
                    name="fullName"
                    placeholder="Your name"
                    value={fullName}
                    onChange={(event) => setFullName(event.target.value)}
                    required
                  />
                </label>

                <label className="trpc-siteFooter__field">
                  <span>Email address</span>
                  <input
                    type="email"
                    name="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                  />
                </label>
              </div>

              <label className="trpc-siteFooter__field">
                <span>Programme of interest</span>
                <select
                  name="programme"
                  value={programme}
                  onChange={(event) => setProgramme(event.target.value)}
                  required
                >
                  <option value="">Select a programme</option>
                  {programmeOptions.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </label>

              <button
                type="submit"
                className="trpc-siteFooter__submit"
                data-candle="green"
                disabled={isSending}
              >
                <span>{isSending ? "Sending..." : "Send Email"}</span>
                <Send size={16} aria-hidden="true" />
              </button>

              {submitStatus && (
                <p className="trpc-siteFooter__submitStatus">{submitStatus}</p>
              )}
            </form>
          </div>
        </div>

        <div className="trpc-siteFooter__middle">
          <div className="trpc-siteFooter__contactPanel">
            <div className="trpc-siteFooter__sectionHeading">
              <span className="trpc-siteFooter__sectionIcon" aria-hidden="true">
                <Phone size={17} />
              </span>
              <h3>Contact</h3>
            </div>

            <div className="trpc-siteFooter__contactList">
              {contactItems.map((item) => {
                const Icon = item.icon;

                const content = (
                  <>
                    <span
                      className="trpc-siteFooter__contactIcon"
                      aria-hidden="true"
                    >
                      <Icon size={17} />
                    </span>

                    <span className="trpc-siteFooter__contactCopy">
                      <span className="trpc-siteFooter__contactLabel">
                        {item.label}
                      </span>
                      <span className="trpc-siteFooter__contactValue">
                        {item.value}
                      </span>
                    </span>
                  </>
                );

                if (item.href) {
                  return (
                    <a
                      key={item.id}
                      href={item.href}
                      className="trpc-siteFooter__contactItem"
                      data-trend={item.trend}
                    >
                      {content}
                    </a>
                  );
                }

                return (
                  <div
                    key={item.id}
                    className="trpc-siteFooter__contactItem"
                    data-trend={item.trend}
                  >
                    {content}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="trpc-siteFooter__linkGrid">
            {footerLinks.map((group) => (
              <nav
                key={group.title}
                className="trpc-siteFooter__linkGroup"
                data-trend={group.trend}
                aria-label={group.title}
              >
                <h3>{group.title}</h3>

                <ul>
                  {group.links.map((link) => (
                    <li key={link.label}>
                      <SmartFooterLink
                        href={link.href}
                        external={link.external}
                        className="trpc-siteFooter__link"
                      >
                        {link.label}
                      </SmartFooterLink>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>

          <div className="trpc-siteFooter__compliancePanel">
            <div className="trpc-siteFooter__sectionHeading">
              <span className="trpc-siteFooter__sectionIcon" aria-hidden="true">
                <ShieldCheck size={17} />
              </span>
              <h3>Accreditations</h3>
            </div>

            <div className="trpc-siteFooter__complianceList">
              {complianceItems.map((item) => (
                <div
                  key={item.id}
                  className="trpc-siteFooter__complianceItem"
                  data-trend={item.trend}
                >
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>

            <div className="trpc-siteFooter__diplomaNote">
              <span aria-hidden="true">
                <GraduationCap size={18} />
              </span>
              <p>
                Complete the full TPC bootcamp sequence and progress toward a
                Diploma in Financial Trading.
              </p>
            </div>
          </div>
        </div>

        <div className="trpc-siteFooter__bottom">
          <div className="trpc-siteFooter__socials" aria-label="Social media">
            {socialLinks.map((social) => (
              <a
                key={social.alt}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="trpc-siteFooter__social"
                aria-label={social.alt}
              >
                <img src={social.src} alt="" loading="lazy" decoding="async" />
              </a>
            ))}
          </div>

          <p className="trpc-siteFooter__copyright">
            © {currentYear} Trade Performance Centre. All rights reserved.
          </p>

          <button
            type="button"
            className="trpc-siteFooter__backToTop"
            onClick={scrollToTop}
            aria-label="Back to top"
          >
            <ArrowUp size={18} aria-hidden="true" />
          </button>

          <p className="trpc-siteFooter__riskDisclosure">
            Trade Performance Centre provides educational training only. We do
            not provide financial advice, investment recommendations, trading
            signals, account management, or guarantees of trading results.
            Trading leveraged products such as forex, CFDs, futures, or crypto
            involves significant risk and may result in loss of capital.
            Students should trade only with money they can afford to lose and
            seek advice from a licensed financial professional where
            appropriate.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default ResponsiveFooter;